import { useContext } from 'react';
import PortalIdParser from 'PortalIdParser';
import I18n from 'I18n';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIButton from 'UIComponents/button/UIButton';
import UIFlex from 'UIComponents/layout/UIFlex';
import UILink from 'UIComponents/link/UILink';
import UITextArea from 'UIComponents/input/UITextArea';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import { useHasScope } from 'automation-ui-core/hooks/authHooks';
import Scopes from 'automation-ui-management/constants/Scopes';
import GeneratedDescriptionRatingButtons from 'automation-ui-management/components/WorkflowNameWithDescription/GeneratedDescriptionRatingButtons';
import { FLOW_TYPES } from 'automation-ui-management/constants/CrmPropertyEnums';
import AILoadingButton from 'ai-components-ui-library/components/buttons/AILoadingButton';
import UsageTrackerContext from 'automation-ui-usage-tracking/UsageTrackerContext';
import { View } from 'foundations-components/components/View';
import { Text } from 'foundations-components/components/Text';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const GenerateDescriptionButton = ({
  descriptionFeedback,
  descriptionRating,
  disabled = false,
  flowType = FLOW_TYPES.WORKFLOW,
  generateDescriptionError,
  generateDescriptionLoading,
  generatedDescription,
  hasSubmittedFeedback,
  isContentAssistanceEnabled = true,
  isFlowEmpty = false,
  onGenerateDescription,
  onRateDescription,
  onSubmitRateDescription
}) => {
  const hasSuperAdminScope = useHasScope(Scopes.SUPER_ADMIN);
  const {
    usageTracker
  } = useContext(UsageTrackerContext);
  const settingsLink = `/ai-settings/${PortalIdParser.get()}`;
  const handleGenerateDescriptionClick = () => {
    onGenerateDescription();
  };
  const handleRatingButtonClick = rating => {
    onRateDescription({
      rating: rating !== descriptionRating ? rating : undefined
    });
  };
  const handleFeedbackChange = event => {
    const {
      value
    } = event.currentTarget;
    if (descriptionRating && value) {
      onRateDescription({
        rating: descriptionRating,
        feedback: value
      });
    }
  };
  const getToolTipMessage = () => {
    if (!isContentAssistanceEnabled && hasSuperAdminScope) {
      return /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "automation-ui-management.WorkflowNameWithDescription.GenerateDescriptionButton.contentAssistance.settingsTooltipAdminV2_jsx",
        elements: {
          Link: UILink,
          Bold: 'b'
        },
        options: {
          external: true,
          href: settingsLink
        }
      });
    }
    if (!isContentAssistanceEnabled && !hasSuperAdminScope) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "automation-ui-management.WorkflowNameWithDescription.GenerateDescriptionButton.contentAssistance.settingsTooltipNonAdminV2"
      });
    }
    if (isFlowEmpty && flowType === FLOW_TYPES.ACTION_SET) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "automation-ui-management.WorkflowNameWithDescription.GenerateDescriptionButton.emptyTooltip.actionSet"
      });
    }
    if (isFlowEmpty) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "automation-ui-management.WorkflowNameWithDescription.GenerateDescriptionButton.emptyTooltip.workflow"
      });
    }
    return '';
  };
  return /*#__PURE__*/_jsxs(UIFlex, {
    align: "center",
    className: "m-top-6",
    justify: "between",
    wrap: "wrap",
    children: [/*#__PURE__*/_jsx(UITooltip, {
      disabled: !disabled,
      title: getToolTipMessage(),
      children: /*#__PURE__*/_jsx(AILoadingButton, {
        usageTracker: usageTracker,
        disabled: disabled,
        failed: generateDescriptionError,
        loading: generateDescriptionLoading,
        onClick: handleGenerateDescriptionClick,
        size: "small",
        children: generatedDescription ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "automation-ui-management.WorkflowNameWithDescription.GenerateDescriptionButton.regenerate"
        }) : /*#__PURE__*/_jsx(FormattedMessage, {
          message: "automation-ui-management.WorkflowNameWithDescription.GenerateDescriptionButton.generate"
        })
      })
    }), generatedDescription && !descriptionRating && /*#__PURE__*/_jsx(GeneratedDescriptionRatingButtons, {
      rating: descriptionRating,
      onRatingButtonClick: handleRatingButtonClick
    }), hasSubmittedFeedback && /*#__PURE__*/_jsx(Text, {
      as: "p",
      marginBlockEnd: "0",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "automation-ui-management.WorkflowNameWithDescription.GenerateDescriptionButton.feedbackReceived"
      })
    }), generatedDescription && descriptionRating && !hasSubmittedFeedback && /*#__PURE__*/_jsx(View, {
      as: "div",
      marginBlockStart: "400",
      padding: "200",
      backgroundColor: "fill-surface-raised",
      borderWidth: "100",
      borderRadius: "100",
      borderColor: "border-core-default",
      borderStyle: "solid",
      inlineSize: "100%",
      children: /*#__PURE__*/_jsxs(View, {
        as: "div",
        marginBlockStart: "200",
        children: [/*#__PURE__*/_jsx(UITextArea, {
          onChange: handleFeedbackChange,
          placeholder: I18n.text('automation-ui-management.WorkflowNameWithDescription.GenerateDescriptionButton.feedbackPlaceholder'),
          rows: 2,
          use: "on-dark",
          value: descriptionFeedback
        }), /*#__PURE__*/_jsxs(UIFlex, {
          align: "center",
          className: "m-top-2",
          justify: "between",
          wrap: "wrap",
          children: [/*#__PURE__*/_jsx(UIButton, {
            className: "m-top-2",
            onClick: () => {
              onSubmitRateDescription();
            },
            size: "extra-small",
            type: "button",
            use: "tertiary-light",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "automation-ui-management.WorkflowNameWithDescription.GenerateDescriptionButton.sendFeedback"
            })
          }), /*#__PURE__*/_jsx(GeneratedDescriptionRatingButtons, {
            rating: descriptionRating,
            onRatingButtonClick: handleRatingButtonClick
          })]
        })]
      })
    })]
  });
};
export default GenerateDescriptionButton;